import React from "react";
import { Link, SpaceBetween, StatusIndicator } from "@cloudscape-design/components";
import { isMajorVersionUpgrade } from "src/core/util/version";
import {
    AT_RISK_LABEL,
    DependencyTableRow,
    guidancePolarity,
    hasRecommendedVersion,
    NOT_AVAILABLE,
    raisesCodeReviews,
} from "./helpers";
import { MajorVersionUpgradePopover } from "./popovers";

export const vendorGuidanceStatusCell = (status?: string): React.ReactNode => {
    const value = status || "-";
    switch (guidancePolarity(value)) {
    case "STRONG_NEGATIVE":
        return <StatusIndicator type="error">{value}</StatusIndicator>;
    case "WEAK_NEGATIVE":
        return <StatusIndicator type="warning">{value}</StatusIndicator>;
    case "POSITIVE":
        return <StatusIndicator type="success">{value}</StatusIndicator>;
    case "NEUTRAL":
    default:
        return value;
    }
};

export const recommendedVersionCell = (
    row: DependencyTableRow,
    showMajorVersionUpgradeInfo = false,
): React.ReactNode => {
    if (row.riskStatus === "HIGH_RISK") {
        return (
            <Link
                external
                href={`https://code.amazon.com/packages/${row.dependencyName}/releases`}
            >
                Seek alternative dependency
            </Link>
        );
    }
    const version = row.recommendedVersion;
    if (!hasRecommendedVersion(version)) return NOT_AVAILABLE;
    if (
        showMajorVersionUpgradeInfo &&
        raisesCodeReviews(row.riskStatus) &&
        isMajorVersionUpgrade(row.version, version)
    ) {
        return (
            <SpaceBetween direction="horizontal" size="xxs" alignItems="center">
                <span>{version}</span>
                <MajorVersionUpgradePopover
                    dependencyName={row.dependencyName}
                    version={row.version}
                    recommendedVersion={version}
                />
            </SpaceBetween>
        );
    }
    return version;
};

export const riskStatusCell = (status?: string): React.ReactNode => {
    const risk = status || "-";
    if (risk === "HIGH_RISK") {
        return (
            <StatusIndicator type="error">
                <b>{risk}</b>
            </StatusIndicator>
        );
    }
    if (risk === "LOW_RISK") {
        return (
            <StatusIndicator type="warning">
                <b>{AT_RISK_LABEL}</b>
            </StatusIndicator>
        );
    }
    if (risk === "UPGRADE_AVAILABLE") {
        return <StatusIndicator type="info">{risk}</StatusIndicator>;
    }
    if (risk === "NO_ACTION_REQUIRED") {
        return <StatusIndicator type="success">{risk}</StatusIndicator>;
    }
    return risk;
};
